import { useEffect, useRef, useState } from "react";
import { Maximize2, Share2, MoreHorizontal, Minus, Shrink, X, Plus, Pencil, Trash2 } from "lucide-react";
import { PageSkeleton } from "@/components/ui/LoadingSpinner";
import { UserIdentityAvatar } from "@/components/ui/UserIdentityAvatar";
import { api } from "@/lib/api";
import {
  usePostits,
  useCreatePostit,
  useUpdatePostit,
  useDeletePostit,
  useUsers,
  useCurrentUser,
} from "@/hooks/useApi";
import type { PostitRecord, PostitChecklistItem, PostitType } from "@/types/api";
import { useFloatingPostit } from "@/components/layout/FloatingPostitContext";

// ── Helpers ────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return "agora";
  if (mins < 60) return `há ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `há ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "ontem";
  return `há ${days} dias`;
}

// ── Color palette ──────────────────────────────────────────────────────────

const PASTEL_COLORS: { value: string; pin: string; label: string }[] = [
  { value: "#fef9c3", pin: "#b5a000", label: "Amarelo" },
  { value: "#dbeafe", pin: "#1e40af", label: "Azul" },
  { value: "#fce7f3", pin: "#be185d", label: "Rosa" },
  { value: "#ede9fe", pin: "#6d28d9", label: "Roxo" },
  { value: "#d1fae5", pin: "#065f46", label: "Verde" },
  { value: "#ffedd5", pin: "#c2410c", label: "Laranja" },
  { value: "#f0f9ff", pin: "#0284c7", label: "Ciano" },
  { value: "#f9fafb", pin: "#374151", label: "Cinza" },
];

function postitStyle(
  postitColor: string | null | undefined,
  identityColor: string | null
): { bg: string; pin: string } {
  if (postitColor) {
    const found = PASTEL_COLORS.find((c) => c.value === postitColor);
    return found ? { bg: found.value, pin: found.pin } : { bg: postitColor, pin: "#374151" };
  }
  switch (identityColor) {
    case "#000000":
    case "#000":
      return { bg: "#fef9c3", pin: "#b5a000" };
    case "#1e40af": return { bg: "#dbeafe", pin: "#1e40af" };
    case "#be185d": return { bg: "#fce7f3", pin: "#be185d" };
    case "#6d28d9": return { bg: "#ede9fe", pin: "#6d28d9" };
    default:        return { bg: "#d1fae5", pin: "#065f46" };
  }
}

// ── Mini-markdown renderer ─────────────────────────────────────────────────

function renderText(text: string): React.ReactNode {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((line, li) => {
    const parts: React.ReactNode[] = [];
    const boldItalicRe = /(\*\*(.+?)\*\*|_(.+?)_)/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = boldItalicRe.exec(line)) !== null) {
      if (m.index > last) parts.push(line.slice(last, m.index));
      if (m[0].startsWith("**")) parts.push(<strong key={m.index}>{m[2]}</strong>);
      else parts.push(<em key={m.index}>{m[3]}</em>);
      last = m.index + m[0].length;
    }
    if (last < line.length) parts.push(line.slice(last));
    return (
      <span key={li}>
        {parts}
        {li < lines.length - 1 && <br />}
      </span>
    );
  });
}

// ── Pin SVG ────────────────────────────────────────────────────────────────

function PinIcon({ color }: { color: string }) {
  return (
    <svg
      viewBox="0 0 18 24"
      width="26"
      height="34"
      style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" }}
      aria-hidden="true"
    >
      <circle cx="9" cy="7" r="6.5" fill={color} />
      <circle cx="7" cy="5.5" r="2" fill="rgba(255,255,255,0.28)" />
      <line
        x1="9" y1="13.5" x2="9" y2="23"
        stroke="#777"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Emoji picker ───────────────────────────────────────────────────────────

const EMOJI_LIST = [
  "😀","😊","🎉","✅","❌","⚠️","🔥","💡","📌","⭐",
  "🎨","🖼️","📅","🚀","💬","✍️","📝","🔑","💰","📊",
  "👍","👎","❤️","🙌","🤔","😅","😂","🎯","📢","🔔",
];

function EmojiPicker({ onSelect }: { onSelect: (emoji: string) => void }) {
  return (
    <div className="absolute z-50 bg-white border border-black/15 shadow-lg p-2 grid grid-cols-6 gap-1 w-52 bottom-full mb-1">
      {EMOJI_LIST.map((e) => (
        <button
          key={e}
          type="button"
          onClick={() => onSelect(e)}
          className="text-lg hover:bg-black/5 rounded p-0.5 transition-colors"
        >
          {e}
        </button>
      ))}
    </div>
  );
}

// ── Format helpers ─────────────────────────────────────────────────────────

function applyFormat(
  textareaRef: React.RefObject<HTMLTextAreaElement>,
  setter: (v: string) => void,
  format: "bold" | "italic"
) {
  const el = textareaRef.current;
  if (!el) return;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const val = el.value;
  const selected = val.slice(start, end) || "texto";
  const wrapped = format === "bold" ? `**${selected}**` : `_${selected}_`;
  const next = val.slice(0, start) + wrapped + val.slice(end);
  setter(next);
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(start + wrapped.length, start + wrapped.length);
  });
}

// ── PostitFormModal ────────────────────────────────────────────────────────

interface PostitFormModalProps {
  postit?: PostitRecord;
  onClose: () => void;
}

function PostitFormModal({ postit, onClose }: PostitFormModalProps) {
  const isEdit = !!postit;
  const [content, setContent] = useState(postit?.content ?? "");
  const [color, setColor] = useState<string | null>(postit?.color ?? null);
  const [type, setType] = useState<PostitType>(postit?.type ?? "text");
  const [isPrivate, setIsPrivate] = useState(postit?.is_private ?? false);
  const [checklistItems, setChecklistItems] = useState<PostitChecklistItem[]>(
    postit?.checklist_items ?? []
  );
  const [newItemText, setNewItemText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const createPostit = useCreatePostit();
  const updatePostit = useUpdatePostit(postit?.id ?? "");

  function addItem() {
    if (!newItemText.trim()) return;
    setChecklistItems([
      ...checklistItems,
      { id: crypto.randomUUID(), text: newItemText.trim(), done: false },
    ]);
    setNewItemText("");
  }

  function removeItem(id: string) {
    setChecklistItems(checklistItems.filter((i) => i.id !== id));
  }

  function insertEmoji(emoji: string) {
    const el = textareaRef.current;
    if (!el) {
      setContent((c) => c + emoji);
    } else {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = content.slice(0, start) + emoji + content.slice(end);
      setContent(next);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + emoji.length, start + emoji.length);
      });
    }
    setShowEmoji(false);
  }

  async function handleSave() {
    if (!content.trim() && type === "text") return;
    if (isEdit) {
      await updatePostit.mutateAsync({
        content: content.trim(),
        color,
        type,
        checklist_items: type === "checklist" ? checklistItems : undefined,
        is_private: isPrivate,
      });
    } else {
      await createPostit.mutateAsync({
        content: content.trim(),
        color,
        type,
        checklist_items: type === "checklist" ? checklistItems : undefined,
        is_private: isPrivate,
      });
    }
    onClose();
  }

  const isPending = createPostit.isPending || updatePostit.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-sm shadow-xl border border-black/10">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/10">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em]">
            {isEdit ? "Editar Postit" : "Novo Postit"}
          </span>
          <button type="button" onClick={onClose} className="text-black/40 hover:text-black">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* Color picker */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-black/50 mr-1">Cor:</span>
            {PASTEL_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                title={c.label}
                onClick={() => setColor(color === c.value ? null : c.value)}
                className="w-5 h-5 rounded-full border-2 transition-all"
                style={{
                  background: c.value,
                  borderColor: color === c.value ? c.pin : "rgba(0,0,0,0.15)",
                  boxShadow: color === c.value ? `0 0 0 1px ${c.pin}` : undefined,
                }}
              />
            ))}
          </div>

          {/* Formatting toolbar */}
          <div className="flex items-center gap-1 relative">
            <button
              type="button"
              title="Negrito"
              onClick={() => applyFormat(textareaRef as React.RefObject<HTMLTextAreaElement>, setContent, "bold")}
              className="px-2 py-0.5 text-[11px] font-bold border border-black/20 hover:border-black transition-colors"
            >
              B
            </button>
            <button
              type="button"
              title="Itálico"
              onClick={() => applyFormat(textareaRef as React.RefObject<HTMLTextAreaElement>, setContent, "italic")}
              className="px-2 py-0.5 text-[11px] italic border border-black/20 hover:border-black transition-colors"
            >
              I
            </button>
            <div className="relative">
              <button
                type="button"
                title="Emoji"
                onClick={() => setShowEmoji((v) => !v)}
                className="px-2 py-0.5 text-[13px] border border-black/20 hover:border-black transition-colors"
              >
                😊
              </button>
              {showEmoji && <EmojiPicker onSelect={insertEmoji} />}
            </div>
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            className="w-full border border-black/20 p-2 text-sm resize-none focus:outline-none focus:border-black"
            rows={5}
            placeholder="Escreva seu postit... (**negrito**, _itálico_)"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            autoFocus={!isEdit}
          />

          {/* Type toggle */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-black/50">Tipo:</span>
            {(["text", "checklist"] as PostitType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={[
                  "px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] border transition-colors",
                  type === t
                    ? "bg-black text-white border-black"
                    : "bg-white text-black/50 border-black/20 hover:border-black",
                ].join(" ")}
              >
                {t === "text" ? "Texto" : "Checklist"}
              </button>
            ))}
          </div>

          {/* Checklist items editor */}
          {type === "checklist" && (
            <div className="space-y-2">
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {checklistItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-2">
                    <span className="text-sm text-black/70 flex-1">{item.text}</span>
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="text-black/30 hover:text-red-500 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  className="flex-1 border border-black/20 px-2 py-1 text-sm focus:outline-none focus:border-black"
                  placeholder="Novo item..."
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
                />
                <button
                  type="button"
                  onClick={addItem}
                  className="p-1.5 bg-black text-white hover:bg-black/80 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Private checkbox */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              className="w-3.5 h-3.5"
            />
            <span className="text-[11px] text-black/60">Privado</span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-black/10">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.06em] border border-black/20 hover:border-black"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={(!content.trim() && type === "text") || isPending}
            className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.06em] bg-black text-white hover:bg-black/80 disabled:opacity-40"
          >
            {isPending ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Share Dropdown ─────────────────────────────────────────────────────────

interface ShareDropdownProps {
  postit: PostitRecord;
  onClose: () => void;
}

function ShareDropdown({ postit, onClose }: ShareDropdownProps) {
  const { data } = useUsers();
  const updatePostit = useUpdatePostit(postit.id);
  const sharedWith: string[] = postit.shared_with ?? [];
  const users = (data ?? []).filter((u) => u.id !== postit.author_id);

  async function toggle(userId: string) {
    const next = sharedWith.includes(userId)
      ? sharedWith.filter((id) => id !== userId)
      : [...sharedWith, userId];
    await updatePostit.mutateAsync({ shared_with: next });
  }

  return (
    <div
      className="absolute right-0 top-full mt-1 bg-white border border-black/15 shadow-xl w-52 z-50 flex flex-col"
      style={{ maxHeight: "280px" }}
    >
      {/* Header fixo */}
      <div className="px-3 py-2 border-b border-black/10 shrink-0">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-black/40">
          Compartilhar com
        </div>
      </div>

      {/* Lista com scroll */}
      <div className="overflow-y-auto flex-1">
        {users.length === 0 && (
          <div className="px-3 py-3 text-[11px] text-black/40">Nenhum usuário disponível</div>
        )}

        {users.map((user) => {
          const isShared = sharedWith.includes(user.id);
          const roleName = user.user_roles?.[0]?.role?.name ?? "";
          return (
            <div
              key={user.id}
              className="flex items-center gap-2.5 px-3 py-2 hover:bg-black/5 cursor-pointer transition-colors"
              onClick={() => toggle(user.id)}
            >
              <UserIdentityAvatar
                user={user}
                sizeClassName="w-7 h-7"
                textClassName="text-[10px] font-bold"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-semibold truncate">{user.full_name ?? "?"}</div>
                {roleName && (
                  <div className="text-[10px] text-black/40 capitalize">{roleName}</div>
                )}
              </div>
              {/* Toggle pill */}
              <div
                className="w-8 h-[18px] rounded-full relative transition-colors shrink-0"
                style={{ background: isShared ? "#000" : "rgba(0,0,0,0.18)" }}
              >
                <div
                  className="w-3 h-3 bg-white rounded-full absolute top-[3px] transition-all"
                  style={{ left: isShared ? "17px" : "3px" }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer fixo com botão fechar */}
      <div className="px-3 py-2 border-t border-black/10 flex justify-end shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="bg-black text-white text-[10px] font-bold uppercase tracking-[0.08em] px-3 py-1.5 hover:bg-black/80 transition-colors"
        >
          Fechar
        </button>
      </div>
    </div>
  );
}

// ── Floating Postit Overlay (named export) ─────────────────────────────────

interface FloatingPostitOverlayProps {
  postit: PostitRecord;
  onClose: () => void;
}

export function FloatingPostitOverlay({ postit, onClose }: FloatingPostitOverlayProps) {
  const style = postitStyle(postit.color, postit.author_identity_color);

  return (
    <div
      className="fixed bottom-7 right-7 w-[260px] z-[100] shadow-2xl border border-black/10"
      style={{ background: style.bg }}
    >
      {/* Header / drag handle */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-black/10 cursor-grab bg-black/[0.02]">
        {/* Grip dots */}
        <svg
          className="w-3 h-3 text-black/35 shrink-0"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <circle cx="9"  cy="5"  r="1.5" />
          <circle cx="9"  cy="12" r="1.5" />
          <circle cx="9"  cy="19" r="1.5" />
          <circle cx="15" cy="5"  r="1.5" />
          <circle cx="15" cy="12" r="1.5" />
          <circle cx="15" cy="19" r="1.5" />
        </svg>
        <UserIdentityAvatar
          user={{
            full_name: postit.author_name ?? undefined,
            identity_color: postit.author_identity_color ?? undefined,
            identity_text_color: postit.author_identity_text_color ?? undefined,
          }}
          sizeClassName="w-4 h-4"
          textClassName="text-[7px] font-bold"
        />
        <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-black/40 flex-1 truncate">
          Postit flutuante
        </span>
        <button type="button" title="Minimizar" className="text-black/30 hover:text-black p-0.5 hover:bg-black/8 rounded-sm transition-colors">
          <Minus className="w-3 h-3" />
        </button>
        <button type="button" title="Voltar ao grid" className="text-black/30 hover:text-black p-0.5 hover:bg-black/8 rounded-sm transition-colors">
          <Shrink className="w-3 h-3" />
        </button>
        <button type="button" title="Fechar" onClick={onClose} className="text-black/30 hover:text-black p-0.5 hover:bg-black/8 rounded-sm transition-colors">
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* Body */}
      <div className="px-3 py-3 text-[12px] leading-[1.6] text-black/80 whitespace-pre-wrap min-h-[60px]">
        {renderText(postit.content)}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-black/8">
        <span className="text-[10px] text-black/35 flex-1">
          {postit.author_name} · {relativeTime(postit.updated_at)}
        </span>
        <UserIdentityAvatar
          user={{
            full_name: postit.author_name ?? undefined,
            identity_color: postit.author_identity_color ?? undefined,
            identity_text_color: postit.author_identity_text_color ?? undefined,
          }}
          sizeClassName="w-4 h-4"
          textClassName="text-[7px] font-bold"
        />
      </div>
    </div>
  );
}

// ── Postit Card ────────────────────────────────────────────────────────────

interface PostitCardProps {
  postit: PostitRecord;
  onFloat: (postit: PostitRecord) => void;
  onDelete: (id: string) => void;
  onEdit: (postit: PostitRecord) => void;
}

function PostitCard({ postit, onFloat, onDelete, onEdit }: PostitCardProps) {
  const style = postitStyle(postit.color, postit.author_identity_color);
  const [localItems, setLocalItems] = useState<PostitChecklistItem[]>(postit.checklist_items ?? []);
  const [hovered, setHovered] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const shareRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    if (!showMore && !showShare) return;
    function handler(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setShowMore(false);
      if (shareRef.current && !shareRef.current.contains(e.target as Node)) setShowShare(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMore, showShare]);

  async function handleChecklistToggle(itemId: string) {
    const updated = localItems.map((item) =>
      item.id === itemId ? { ...item, done: !item.done } : item
    );
    setLocalItems(updated);
    try {
      await api.patch<PostitRecord>(`/postits/${postit.id}`, { checklist_items: updated });
    } catch {
      setLocalItems(localItems);
    }
  }

  return (
    <div
      className="break-inside-avoid mb-4 border border-black/8"
      style={{
        background: style.bg,
        boxShadow: hovered
          ? "4px 8px 20px rgba(0,0,0,0.18)"
          : "2px 5px 14px rgba(0,0,0,0.13), 0 1px 3px rgba(0,0,0,0.08)",
        // transform cria stacking context; quando um dropdown está aberto,
        // aplicar position+zIndex garante que o dropdown apareça acima dos outros cards
        transform: hovered ? "translateY(-2px) rotate(0.3deg)" : undefined,
        transition: "box-shadow 130ms, transform 130ms",
        position: "relative",
        zIndex: (showShare || showMore) ? 10 : undefined,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Pin — inside the card, centered at top */}
      <div className="flex justify-center pt-2.5 pb-0">
        <PinIcon color={style.pin} />
      </div>

      {/* Author / time label */}
      <div className="flex items-center gap-2 px-3.5 pt-1.5 pb-0">
        <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-black/40 truncate flex-1">
          {postit.author_name} · {relativeTime(postit.updated_at)}
        </span>
        {postit.is_private && (
          <span className="text-[9px] font-bold uppercase tracking-[0.06em] px-1.5 py-0.5 bg-black/8 text-black/50">
            Privado
          </span>
        )}
      </div>

      {/* Content body */}
      <div className="px-3.5 pt-2 pb-3">
        {postit.type === "checklist" ? (
          <div className="space-y-1.5">
            {postit.content && (
              <p className="text-[11px] font-bold text-black/50 uppercase tracking-[0.08em] mb-1.5">
                {renderText(postit.content)}
              </p>
            )}
            {localItems.map((item) => (
              <label
                key={item.id}
                className="flex items-start gap-2 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={item.done}
                  onChange={() => handleChecklistToggle(item.id)}
                  className="mt-0.5 w-3.5 h-3.5 shrink-0"
                  style={{ accentColor: "#000" }}
                />
                <span
                  className={[
                    "text-[12px] text-black/80",
                    item.done ? "line-through opacity-40" : "",
                  ].join(" ")}
                >
                  {item.text}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-[13px] leading-[1.55] text-black/80 whitespace-pre-wrap">
            {renderText(postit.content)}
          </p>
        )}
      </div>

      {/* Footer with tint */}
      <div
        className="flex items-center justify-between px-3.5 py-2 border-t border-black/8"
        style={{ background: "rgba(0,0,0,0.04)" }}
      >
        {/* Avatar cluster */}
        <div className="flex items-center">
          <UserIdentityAvatar
            user={{
              full_name: postit.author_name ?? undefined,
              identity_color: postit.author_identity_color ?? undefined,
              identity_text_color: postit.author_identity_text_color ?? undefined,
            }}
            sizeClassName="w-5 h-5"
            textClassName="text-[8px] font-bold"
          />
          {(postit.shared_with ?? []).slice(0, 3).map((userId, i) => (
            <div key={userId} style={{ marginLeft: "-6px", zIndex: i + 1 }}>
              <UserIdentityAvatar
                user={{ full_name: userId }}
                sizeClassName="w-5 h-5"
                textClassName="text-[8px] font-bold"
              />
            </div>
          ))}
        </div>

        {/* Action buttons — always visible */}
        <div className="flex items-center gap-0">
          {/* Edit */}
          <button
            type="button"
            title="Editar"
            onClick={(e) => { e.stopPropagation(); onEdit(postit); }}
            className="w-[26px] h-[26px] flex items-center justify-center text-black/35 hover:text-black hover:bg-black/8 rounded-sm transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>

          {/* Float */}
          <button
            type="button"
            title="Flutuar"
            onClick={(e) => { e.stopPropagation(); onFloat(postit); }}
            className="w-[26px] h-[26px] flex items-center justify-center text-black/35 hover:text-black hover:bg-black/8 rounded-sm transition-colors"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>

          {/* Share */}
          <div ref={shareRef} className="relative">
            <button
              type="button"
              title="Compartilhar"
              onClick={(e) => { e.stopPropagation(); setShowShare((v) => !v); setShowMore(false); }}
              className={[
                "w-[26px] h-[26px] flex items-center justify-center rounded-sm transition-colors",
                showShare
                  ? "bg-black/10 text-black"
                  : "text-black/35 hover:text-black hover:bg-black/8",
              ].join(" ")}
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
            {showShare && (
              <ShareDropdown postit={postit} onClose={() => setShowShare(false)} />
            )}
          </div>

          {/* More */}
          <div ref={moreRef} className="relative">
            <button
              type="button"
              title="Mais opções"
              onClick={(e) => { e.stopPropagation(); setShowMore((v) => !v); setShowShare(false); }}
              className={[
                "w-[26px] h-[26px] flex items-center justify-center rounded-sm transition-colors",
                showMore
                  ? "bg-black/10 text-black"
                  : "text-black/35 hover:text-black hover:bg-black/8",
              ].join(" ")}
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
            {showMore && (
              <div className="absolute right-0 bottom-full mb-1 bg-white border border-black/15 shadow-lg z-50 w-36">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-[12px] hover:bg-black/5 transition-colors"
                  onClick={(e) => { e.stopPropagation(); onEdit(postit); setShowMore(false); }}
                >
                  <Pencil className="w-3 h-3 text-black/40" />
                  Editar
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-[12px] text-red-600 hover:bg-red-50 transition-colors"
                  onClick={(e) => { e.stopPropagation(); onDelete(postit.id); setShowMore(false); }}
                >
                  <Trash2 className="w-3 h-3" />
                  Excluir
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Filter types ───────────────────────────────────────────────────────────

type PostitsScope = "todos" | "meus" | "compartilhados";

// ── Main PostitsList component ─────────────────────────────────────────────

export default function PostitsList() {
  const [scope, setScope] = useState<PostitsScope>("todos");
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingPostit, setEditingPostit] = useState<PostitRecord | null>(null);

  const { setFloatingPostit } = useFloatingPostit();

  const { data, isLoading } = usePostits();
  const deletePostit = useDeletePostit();
  const currentUserQuery = useCurrentUser();
  const myId = currentUserQuery.data?.id;

  const allPostits = data?.postits ?? [];

  const filtered = allPostits.filter((p) => {
    if (scope === "meus")
      return myId ? p.author_id === myId : !!p.author_id;
    if (scope === "compartilhados")
      // Posts where I'm in shared_with but NOT the author
      return myId
        ? (p.shared_with ?? []).includes(myId) && p.author_id !== myId
        : (p.shared_with ?? []).length > 0;
    return true;
  });

  async function handleDelete(id: string) {
    const confirmed = window.confirm("Excluir este postit?");
    if (!confirmed) return;
    await deletePostit.mutateAsync(id);
    setFloatingPostit(null);
  }

  if (isLoading) return <PageSkeleton />;

  const tabConfig: { key: PostitsScope; label: string }[] = [
    { key: "todos",          label: `Todos (${allPostits.length})` },
    { key: "meus",           label: `Meus (${allPostits.filter((p) => myId ? p.author_id === myId : !!p.author_id).length})` },
    { key: "compartilhados", label: `Compartilhados comigo (${allPostits.filter((p) => myId ? (p.shared_with ?? []).includes(myId) && p.author_id !== myId : (p.shared_with ?? []).length > 0).length})` },
  ];

  return (
    <div className="space-y-4">
      {/* ── Filter tabs bar ── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-0">
          {tabConfig.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setScope(key)}
              className={[
                "px-4 py-2 text-[11px] font-bold uppercase tracking-[0.06em] border transition-colors",
                scope === key
                  ? "bg-black text-white border-black"
                  : "bg-white text-black/50 border-black/20 hover:border-black hover:text-black",
              ].join(" ")}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowNewModal(true)}
          className="bg-black text-white hover:bg-black/80 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.06em] flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" />
          Novo Postit
        </button>
      </div>

      {/* ── Masonry grid ── */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-black/40">
          <p className="text-sm">Nenhum postit ainda. Crie o primeiro!</p>
        </div>
      ) : (
        <div style={{ columns: 4, columnGap: "12px" }}>
          {filtered.map((postit) => (
            <PostitCard
              key={postit.id}
              postit={postit}
              onFloat={setFloatingPostit}
              onDelete={handleDelete}
              onEdit={setEditingPostit}
            />
          ))}
        </div>
      )}

      {/* ── New Postit Modal ── */}
      {showNewModal && (
        <PostitFormModal onClose={() => setShowNewModal(false)} />
      )}

      {/* ── Edit Postit Modal ── */}
      {editingPostit && (
        <PostitFormModal
          postit={editingPostit}
          onClose={() => setEditingPostit(null)}
        />
      )}
    </div>
  );
}
